/**
 * 打卡频率规则与积分计算（见方案 2.3、3.4.1）。
 *
 * 这是统计口径的唯一来源：进度、连续打卡、积分都从这里派生，
 * 避免"同一件事在不同页面算出不同数字"。
 */
import {
  DEFAULT_GRACE_HOURS,
  type Recurrence,
} from './constants.js';
import {
  addDays,
  compareDate,
  dayOfMonth,
  diffDays,
  lastDayOfMonth,
  weekdayOf,
} from './time.js';

/** 频率规则的可判定子集（与 habit 表字段一一对应）。 */
export interface RecurrenceRule {
  recurrence: Recurrence;
  weekdays: number[];
  monthDays: number[];
  intervalDays: number | null;
  activeFrom: string;
  activeTo: string | null;
}

/** 校验规则自身是否自洽（与 zod 校验互补，供服务端二次防御）。 */
export function validateRecurrenceRule(rule: RecurrenceRule): string | null {
  if (rule.recurrence === 'weekly' && rule.weekdays.length === 0) return '每周频率需要选择星期';
  if (rule.recurrence === 'monthly' && rule.monthDays.length === 0) return '每月频率需要选择日期';
  if (rule.recurrence === 'custom' && (!rule.intervalDays || rule.intervalDays < 1)) {
    return '自定义频率需要填写间隔天数';
  }
  if (rule.activeTo && compareDate(rule.activeFrom, rule.activeTo) > 0) {
    return '生效开始日期不能晚于结束日期';
  }
  return null;
}

/**
 * 判断某个本地日期是否落在该频率规则的计划内。
 * 注意：判断的是"这一天要不要打卡"，与是否已完成无关。
 */
export function isScheduledOn(rule: RecurrenceRule, localDate: string): boolean {
  if (compareDate(localDate, rule.activeFrom) < 0) return false;
  if (rule.activeTo && compareDate(localDate, rule.activeTo) > 0) return false;

  switch (rule.recurrence) {
    case 'daily':
      return true;

    case 'weekly':
      return rule.weekdays.includes(weekdayOf(localDate));

    case 'monthly': {
      const dom = dayOfMonth(localDate);
      // -1 表示"每月最后一天"
      if (rule.monthDays.includes(-1) && dom === lastDayOfMonth(localDate)) return true;
      return rule.monthDays.includes(dom);
    }

    case 'custom': {
      const interval = rule.intervalDays ?? 1;
      const elapsed = diffDays(rule.activeFrom, localDate);
      return elapsed >= 0 && elapsed % interval === 0;
    }

    /**
     * 一次性：**只在 `activeFrom` 那一天**。
     *
     * ⚠️ 这个分支是必需的，不能靠 `default: false` 兜底 ——
     * 早期 `switch` 里没有 `once`，落进 default 会返回 false，
     * 结果**一次性任务永远不会被排期、永远不出现在任何清单里**
     * （而且不报错，只是"什么都没有"）。作业明细正是用 `once`。
     */
    case 'once':
      return compareDate(localDate, rule.activeFrom) === 0;

    default:
      return false;
  }
}

/**
 * 生成 [from, to] 区间内所有应打卡的日期（本地日期字符串）。
 * 用于定时任务补生成实例、以及进度计算的分母。
 *
 * 上限保护：单次最多返回 MAX_DUE_DATE_SPAN 天，避免误传区间导致循环失控。
 * 判定必须放在循环**开头**，否则会先推入一天再退出，实际返回 MAX+1 条。
 */
export const MAX_DUE_DATE_SPAN = 400;

export function generateDueDates(rule: RecurrenceRule, from: string, to: string): string[] {
  if (compareDate(from, to) > 0) return [];
  const start = compareDate(from, rule.activeFrom) < 0 ? rule.activeFrom : from;
  const end = rule.activeTo && compareDate(to, rule.activeTo) > 0 ? rule.activeTo : to;
  if (compareDate(start, end) > 0) return [];

  const out: string[] = [];
  let cursor = start;
  let guard = 0;
  while (compareDate(cursor, end) <= 0) {
    if (guard++ >= MAX_DUE_DATE_SPAN) break;
    if (isScheduledOn(rule, cursor)) out.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return out;
}

/**
 * 计算一次打卡能得多少积分。
 *
 * 规则（见方案 3.4.1）：
 * - **有量化目标**：`floor(pointsPerUnit × valueDone ÷ unitSize)`
 * - **无量化目标**：`pointsPerUnit`（按"一次"计，**忽略 unitSize**）
 * - 补卡：可按 `makeupFactor` 打折（默认不打折，由调用方传）
 * - 结果受 `dailyPointsCap` 限制
 *
 * ## `unitSize`（N）是什么
 *
 * 账本（`points_ledger.delta` / `points_account.balance`）是 `integer`，
 * 改成小数要动所有求和与不变式，而且 0.5 分 × 1 单位会被末尾的 `floor` 变成
 * **0 分** —— 孩子做了事反而拿不到分。所以把"小数分值"改写成**整数对整数**：
 *
 *     「每 N 个单位得 M 分」   ←→   M / N 分每单位
 *
 * 「每 2 分钟得 1 分」与「0.5 分/分钟」语义完全一样。
 * **`unitSize = 1` 时公式退化成 `floor(pointsPerUnit × valueDone)`，
 * 与改动前逐例等价**（有回归测试），所以存量数据的行为一分不差。
 *
 * ## 没设目标值时为什么忽略 `unitSize`（这是一个必须想清楚的边界）
 *
 * `valueDone` 为空时按"**一次**"计。若照搬 `floor(M × 1 ÷ N)`，那么
 * 「每 2 单位得 1 分」+ 没设目标 → `floor(1/2)` → **0 分**：
 * 孩子完成了任务却拿不到分 —— 与"0 分 = 明确不计分"的语义直接冲突
 * （见下方 `pointsPerUnit <= 0` 那条），家长会以为系统坏了。
 *
 * 更根本的理由是**语义**：「每 N 单位得 M 分」这句话里的"N 单位"只有在
 * **存在量化目标**时才成立 —— 没有目标就没有"单位"可数，这次完成就是"一次"。
 * 所以没有量化目标时按「**每完成一次得 M 分**」计（忽略 N），
 * 界面上也照这句话显示（`describePointsRule`）。
 *
 * @param params.valueDone    实际完成值；为空/0 时按"一次"计
 * @param params.targetValue  该 habit 是否设了量化目标（`null` = 没设）。
 *                            只用来判定走哪条分支 —— 公式本身不用目标值。
 */
export function calculateCheckinPoints(params: {
  pointsPerUnit: number;
  valueDone: number | null;
  /** 每多少个单位给一次 `pointsPerUnit`；必须 ≥ 1，非法值按 1 处理。 */
  unitSize?: number | null;
  /**
   * 该 habit 是否设了量化目标（`null` = 没设）。
   *
   * 只用来判定走哪条分支 —— 公式本身不用目标值。
   * 刻意接受 `undefined`：调用点从 DTO 上取字段时它天然可能是 `undefined`，
   * 而 `undefined` 与 `null` 在这里**语义相同**（都没设目标）。
   */
  targetValue?: number | null | undefined;
  dailyPointsCap?: number | null;
  isMakeup?: boolean;
  makeupFactor?: number;
}): number {
  const {
    pointsPerUnit,
    valueDone,
    unitSize,
    targetValue,
    dailyPointsCap,
    isMakeup = false,
    makeupFactor = 1,
  } = params;
  if (pointsPerUnit <= 0) return 0;

  /**
   * N 的兜底：`undefined` / `null` / 非有限值 / `< 1` 一律按 **1** 处理。
   *
   * 为什么是"按 1"而不是报错：这里是**纯函数**，调用点分布在打卡完成、家长验收、
   * 补卡三条入账路径上，任何一条抛错都会把"孩子今天的打卡"打成 500。
   * N 的合法性由 zod（请求层）、表单（界面层）与 DB CHECK（写入层）三层挡住；
   * 这里退回 1 是**最后一道不崩的兜底**，而且 1 恰好等于改动前的语义。
   */
  const n = Number.isFinite(unitSize) && (unitSize as number) >= 1 ? Math.floor(unitSize as number) : 1;

  // 有没有量化目标，决定 valueDone 为空时那一次算不算"1 个单位"
  const hasTarget = targetValue !== null && targetValue !== undefined;

  let points: number;
  if (!hasTarget) {
    // 没有量化目标 → 这次完成就是"一次"，直接给 M 分（忽略 N，理由见上面的注释）
    points = pointsPerUnit;
  } else {
    const units = valueDone && valueDone > 0 ? valueDone : 1;
    points = (pointsPerUnit * units) / n;
  }

  if (isMakeup && makeupFactor !== 1) {
    points = Math.floor(points * makeupFactor);
  }

  if (dailyPointsCap !== null && dailyPointsCap !== undefined) {
    points = Math.min(points, dailyPointsCap);
  }

  return Math.max(0, Math.floor(points));
}

/**
 * 把一条积分规则写成**人类可读的一句话**（家长端 / 学生端所有展示点共用）。
 *
 * 一个概念只留一处文案来源：卡片、统计页、表单预览都调它，
 * 才不会出现"A 页面说 0.5 分/分钟、B 页面说 1 分/单位"这种自相矛盾。
 *
 * 说的三件事：
 *  - 有量化目标：`每 N 分钟得 1 分 · 完成 30 分钟可得 15 分`（N = 1 时省略"N"）
 *  - 没有量化目标：`每完成一次得 2 分`（**不提 N**，因为没目标就没有"单位"）
 *  - `pointsPerUnit <= 0`：`不计分（只记录完成情况）`
 */
export function describePointsRule(params: {
  pointsPerUnit: number;
  /** 每多少个单位得 `pointsPerUnit` 分；省略 = 1。 */
  unitSize?: number | null;
  targetValue?: number | null;
  unit?: string | null;
  dailyPointsCap?: number | null;
}): string {
  const { pointsPerUnit, targetValue, unit, dailyPointsCap } = params;
  if (!(pointsPerUnit > 0)) return '不计分（只记录完成情况）';

  const n = Number.isFinite(params.unitSize) && (params.unitSize as number) >= 1
    ? Math.floor(params.unitSize as number)
    : 1;
  const capText =
    dailyPointsCap !== null && dailyPointsCap !== undefined ? `（单日最多 ${dailyPointsCap} 分）` : '';
  /**
   * 单位缺失时用「单位」占位。
   *
   * 为什么不让它是空串：会拼出「每 2得 1 分」「完成 4 可得 2 分」这种**断句**。
   * 正常路径上单位不会缺（`ck_habit_target_unit` 要求设了目标就必须有单位），
   * 但这个函数也用在"家长刚勾上目标值、还没填单位"的实时预览里，那时确实为空。
   */
  const unitText = (unit ?? '').trim() || '单位';

  const hasTarget = targetValue !== null && targetValue !== undefined && targetValue > 0;

  // 规则本身：「每 2 分钟得 1 分」/「每 1 分钟得 2 分」（N=1 时只说「每分钟得 2 分」）
  const ruleText = n > 1 ? `每 ${n}${unitText}得 ${pointsPerUnit} 分` : `每${unitText || '单位'}得 ${pointsPerUnit} 分`;

  if (!hasTarget) {
    // 没目标 → 忽略 N（与 calculateCheckinPoints 同一套语义）
    return `每完成一次得 ${pointsPerUnit} 分${capText}`;
  }

  const points = calculateCheckinPoints({
    pointsPerUnit,
    unitSize: n,
    targetValue,
    valueDone: targetValue,
    dailyPointsCap,
  });
  return `${ruleText} · 完成 ${targetValue}${unitText} 可得 ${points} 分${capText}`;
}

/**
 * 判断某个已错过的打卡是否还在补卡宽限期内（见方案 2.3）。
 * 宽限期从"当日 24:00"起算 graceHours 小时。
 */
export function isWithinGrace(
  dueDate: string,
  today: string,
  graceHours: number = DEFAULT_GRACE_HOURS,
): boolean {
  const elapsedDays = diffDays(dueDate, today);
  if (elapsedDays < 0) return false; // 还没到日期
  if (elapsedDays === 0) return true; // 当天
  const graceDays = graceHours / 24;
  return elapsedDays <= graceDays;
}

/**
 * 连续打卡天数（streak）。
 *
 * 规则（见方案 2.3）：
 * - 只统计 `done` 且 **非补卡** 的实例
 * - 遇到"计划内但未完成"的日期即中断
 * - **非计划日不断连**（例如每周一三五的计划，周二不算断）
 *
 * @param scheduledDates 计划内日期（升序），来自 generateDueDates
 * @param doneDates      实际完成（非补卡）的日期集合
 * @param today          家庭时区下的今天，晚于今天的计划日不参与计算
 */
export function calculateStreak(
  scheduledDates: string[],
  doneDates: Iterable<string>,
  today: string,
): number {
  const done = new Set(doneDates);
  let streak = 0;
  for (let i = scheduledDates.length - 1; i >= 0; i--) {
    const date = scheduledDates[i]!;
    if (compareDate(date, today) > 0) continue; // 未来不参与
    if (done.has(date)) {
      streak++;
    } else if (date === today) {
      // 今天还没做不算断连，继续往前看
      continue;
    } else {
      break;
    }
  }
  return streak;
}
